import { useEffect, useRef, useState, type PointerEvent } from 'react'
import {
  ArrowDown,
  ArrowUp,
  BringToFront,
  Clock3,
  Copy,
  Image as ImageIcon,
  ListPlus,
  Maximize2,
  Move,
  PaintBucket,
  Plus,
  SendToBack,
  Trash2,
  Type,
  Video,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { canvasFontSize } from './canvas-font-size'
import { DynamicTimePreview } from './dynamic-time'
import { imageMotionClass, imageMotionStyle } from './image-motion'
import { SignageFontSelect } from './signage-fonts'

export type BuilderZoneType =
  'IMAGE' | 'VIDEO' | 'TEXT' | 'TICKER' | 'DYNAMIC_TIME'

export interface BuilderZone {
  id: string
  name: string
  type: BuilderZoneType
  x: number
  y: number
  width: number
  height: number
  zIndex: number
  style?: Record<string, unknown>
}

interface Interaction {
  id: string
  mode: 'move' | 'resize'
  startX: number
  startY: number
  startZone: BuilderZone
  canvasWidth: number
  canvasHeight: number
}

interface VisualLayoutBuilderProps {
  orientation: 'LANDSCAPE' | 'PORTRAIT'
  zones: BuilderZone[]
  onChange: (zones: BuilderZone[]) => void
  freeform: boolean
  media?: {
    id: string
    name: string
    kind: 'IMAGE' | 'VIDEO'
    thumbnailUrl: string | null
  }[]
}

const zoneIcons = {
  IMAGE: ImageIcon,
  VIDEO: Video,
  TEXT: Type,
  TICKER: Move,
  DYNAMIC_TIME: Clock3,
}

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value))

const rounded = (value: number) => Math.round(value * 200) / 200

function styleString(
  style: Record<string, unknown> | undefined,
  key: string,
  fallback: string
) {
  return typeof style?.[key] === 'string' ? style[key] : fallback
}

function styleNumber(
  style: Record<string, unknown> | undefined,
  key: string,
  fallback: number
) {
  return typeof style?.[key] === 'number' ? style[key] : fallback
}

function animationClass(style: Record<string, unknown> | undefined) {
  switch (styleString(style, 'entryAnimation', 'NONE')) {
    case 'FADE_IN':
      return 'dotit-animate-fade'
    case 'SLIDE_LEFT':
      return 'dotit-animate-slide-left'
    case 'SLIDE_RIGHT':
      return 'dotit-animate-slide-right'
    case 'SLIDE_TOP':
      return 'dotit-animate-slide-top'
    case 'SLIDE_BOTTOM':
      return 'dotit-animate-slide-bottom'
    case 'ZOOM_IN':
      return 'dotit-animate-zoom'
    default:
      return ''
  }
}

function displayText(text: string, listStyle: string) {
  const marker = {
    BULLET: '•',
    CHECK: '✓',
    ARROW: '→',
    STAR: '★',
  }[listStyle]
  if (!marker) return text
  return text
    .split('\n')
    .map((line) => (line.trim() ? `${marker}  ${line}` : ''))
    .join('\n')
}

export function VisualLayoutBuilder({
  orientation,
  zones,
  onChange,
  freeform,
  media = [],
}: VisualLayoutBuilderProps) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const interaction = useRef<Interaction | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(
    zones[0]?.id ?? null
  )
  const selected = zones.find((zone) => zone.id === selectedId) ?? zones[0]

  useEffect(() => {
    if (!selected && zones[0]) setSelectedId(zones[0].id)
  }, [selected, zones])

  const replaceZone = (id: string, patch: Partial<BuilderZone>) => {
    onChange(
      zones.map((zone) => (zone.id === id ? { ...zone, ...patch } : zone))
    )
  }

  const replaceStyle = (id: string, patch: Record<string, unknown>) => {
    onChange(
      zones.map((zone) =>
        zone.id === id
          ? { ...zone, style: { ...(zone.style ?? {}), ...patch } }
          : zone
      )
    )
  }

  const beginInteraction = (
    event: PointerEvent<HTMLElement>,
    zone: BuilderZone,
    mode: 'move' | 'resize'
  ) => {
    if (!freeform) return
    event.preventDefault()
    event.stopPropagation()
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return
    event.currentTarget.setPointerCapture(event.pointerId)
    setSelectedId(zone.id)
    interaction.current = {
      id: zone.id,
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startZone: zone,
      canvasWidth: rect.width,
      canvasHeight: rect.height,
    }
  }

  const continueInteraction = (event: PointerEvent<HTMLElement>) => {
    const current = interaction.current
    if (!current) return
    event.preventDefault()
    const deltaX = (event.clientX - current.startX) / current.canvasWidth
    const deltaY = (event.clientY - current.startY) / current.canvasHeight
    if (current.mode === 'move') {
      replaceZone(current.id, {
        x: rounded(
          clamp(current.startZone.x + deltaX, 0, 1 - current.startZone.width)
        ),
        y: rounded(
          clamp(current.startZone.y + deltaY, 0, 1 - current.startZone.height)
        ),
      })
      return
    }
    replaceZone(current.id, {
      width: rounded(
        clamp(current.startZone.width + deltaX, 0.05, 1 - current.startZone.x)
      ),
      height: rounded(
        clamp(current.startZone.height + deltaY, 0.05, 1 - current.startZone.y)
      ),
    })
  }

  const endInteraction = () => {
    interaction.current = null
  }

  const addZone = (
    type: BuilderZoneType,
    styleOverrides: Record<string, unknown> = {}
  ) => {
    const offset = (zones.length % 6) * 0.04
    const id = crypto.randomUUID()
    const zone: BuilderZone = {
      id,
      name: `${type.charAt(0)}${type.slice(1).toLowerCase()} ${zones.length + 1}`,
      type,
      x: rounded(clamp(0.08 + offset, 0, 0.7)),
      y: rounded(clamp(0.08 + offset, 0, 0.7)),
      width: type === 'TICKER' ? 0.84 : 0.4,
      height: type === 'TICKER' ? 0.12 : 0.28,
      zIndex: zones.length,
      style: {
        text:
          type === 'TICKER'
            ? 'Breaking news and announcements move here'
            : type === 'TEXT'
              ? 'Double-click the text controls to edit'
              : '',
        color: '#ffffff',
        background:
          type === 'TEXT' || type === 'TICKER'
            ? '#dc2626'
            : type === 'DYNAMIC_TIME'
              ? '#003f39'
              : '#18181b',
        fontSize: type === 'DYNAMIC_TIME' ? 96 : type === 'TICKER' ? 28 : 42,
        lineHeight: 1.2,
        fontWeight: 700,
        fontFamily: 'Hind Siliguri',
        textAlign: 'CENTER',
        objectFit: 'COVER',
        entryAnimation: 'FADE_IN',
        animationDurationMs: 650,
        entryDelayMs: 0,
        verticalAlign: 'CENTER',
        padding: 16,
        listStyle: 'NONE',
        tickerSpeed: 70,
        timerMode: 'COUNTDOWN',
        targetTime: '13:15',
        timezone: 'Asia/Dhaka',
        locale: 'bn-BD',
        hourCycle: 'H12',
        showSeconds: true,
        imageMotion: 'NONE',
        motionDurationMs: 12_000,
        motionIntensity: 5,
        motionDirection: 'LEFT_RIGHT',
        motionFocalPoint: 'CENTER',
        motionLoop: true,
        ...styleOverrides,
      },
    }
    onChange([...zones, zone])
    setSelectedId(id)
  }

  const addCanvasBackground = () => {
    const existing = zones.find(
      (zone) => zone.style?.isCanvasBackground === true
    )
    if (existing) {
      setSelectedId(existing.id)
      return
    }
    const id = crypto.randomUUID()
    const background: BuilderZone = {
      id,
      name: 'Canvas background',
      type: 'TEXT',
      x: 0,
      y: 0,
      width: 1,
      height: 1,
      zIndex: 0,
      style: {
        text: '',
        color: '#ffffff',
        background: '#18181b',
        fontSize: 32,
        lineHeight: 1.2,
        fontWeight: 400,
        fontFamily: 'Hind Siliguri',
        textAlign: 'CENTER',
        verticalAlign: 'CENTER',
        padding: 0,
        entryAnimation: 'NONE',
        animationDurationMs: 650,
        entryDelayMs: 0,
        listStyle: 'NONE',
        isCanvasBackground: true,
      },
    }
    onChange([
      background,
      ...zones.map((zone, index) => ({ ...zone, zIndex: index + 1 })),
    ])
    setSelectedId(id)
  }

  const removeSelected = () => {
    if (!freeform || !selected || zones.length === 1) return
    const remaining = zones
      .filter((zone) => zone.id !== selected.id)
      .map((zone, index) => ({ ...zone, zIndex: index }))
    onChange(remaining)
    setSelectedId(remaining[0]?.id ?? null)
  }

  const moveLayer = (direction: 'forward' | 'backward' | 'front' | 'back') => {
    if (!freeform || !selected) return
    const ordered = [...zones].sort((a, b) => a.zIndex - b.zIndex)
    const currentIndex = ordered.findIndex((zone) => zone.id === selected.id)
    if (currentIndex < 0) return
    const targetIndex =
      direction === 'front'
        ? ordered.length - 1
        : direction === 'back'
          ? 0
          : direction === 'forward'
            ? Math.min(ordered.length - 1, currentIndex + 1)
            : Math.max(0, currentIndex - 1)
    if (targetIndex === currentIndex) return
    const next = [...ordered]
    const [layer] = next.splice(currentIndex, 1)
    next.splice(targetIndex, 0, layer!)
    onChange(next.map((zone, index) => ({ ...zone, zIndex: index })))
  }

  const duplicateSelected = () => {
    if (!freeform || !selected || zones.length >= 20) return
    const id = crypto.randomUUID()
    const duplicate: BuilderZone = {
      ...selected,
      id,
      name: `${selected.name} copy`,
      x: rounded(clamp(selected.x + 0.025, 0, 1 - selected.width)),
      y: rounded(clamp(selected.y + 0.025, 0, 1 - selected.height)),
      zIndex: Math.max(...zones.map((zone) => zone.zIndex), 0) + 1,
      style: { ...(selected.style ?? {}), isCanvasBackground: false },
    }
    onChange([...zones, duplicate])
    setSelectedId(id)
  }

  const alignSelected = (
    alignment: 'LEFT' | 'HCENTER' | 'RIGHT' | 'TOP' | 'VCENTER' | 'BOTTOM'
  ) => {
    if (!selected) return
    const patch: Partial<BuilderZone> = {}
    if (alignment === 'LEFT') patch.x = 0
    if (alignment === 'HCENTER') patch.x = rounded((1 - selected.width) / 2)
    if (alignment === 'RIGHT') patch.x = rounded(1 - selected.width)
    if (alignment === 'TOP') patch.y = 0
    if (alignment === 'VCENTER') patch.y = rounded((1 - selected.height) / 2)
    if (alignment === 'BOTTOM') patch.y = rounded(1 - selected.height)
    replaceZone(selected.id, patch)
  }

  const fillCanvas = () => {
    if (!selected) return
    replaceZone(selected.id, { x: 0, y: 0, width: 1, height: 1 })
  }

  const nudgeSelected = (deltaX: number, deltaY: number) => {
    if (!selected || !freeform) return
    replaceZone(selected.id, {
      x: rounded(clamp(selected.x + deltaX, 0, 1 - selected.width)),
      y: rounded(clamp(selected.y + deltaY, 0, 1 - selected.height)),
    })
  }

  const numericGeometry = (
    property: 'x' | 'y' | 'width' | 'height',
    value: number
  ) => {
    if (!selected) return
    const normalized = value / 100
    const maximum =
      property === 'x'
        ? 1 - selected.width
        : property === 'y'
          ? 1 - selected.height
          : property === 'width'
            ? 1 - selected.x
            : 1 - selected.y
    replaceZone(selected.id, {
      [property]: rounded(
        clamp(
          normalized,
          property === 'width' || property === 'height' ? 0.05 : 0,
          maximum
        )
      ),
    })
  }

  return (
    <div className='grid grid-cols-1 gap-4'>
      <div className='space-y-3'>
        {freeform ? (
          <div className='flex flex-wrap gap-2 rounded-lg border bg-muted/30 p-2'>
            {(
              [
                'IMAGE',
                'VIDEO',
                'TEXT',
                'TICKER',
                'DYNAMIC_TIME',
              ] as BuilderZoneType[]
            ).map((type) => {
              const Icon = zoneIcons[type]
              const disabled =
                zones.length >= 20 ||
                (type === 'VIDEO' &&
                  zones.some((zone) => zone.type === 'VIDEO'))
              return (
                <Button
                  key={type}
                  type='button'
                  variant='outline'
                  size='sm'
                  disabled={disabled}
                  onClick={() => addZone(type)}
                >
                  <Icon className='size-4' />
                  Add {type.toLowerCase()}
                </Button>
              )
            })}
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={zones.length >= 20}
              onClick={() =>
                addZone('TEXT', {
                  text: 'First point\nSecond point\nThird point',
                  listStyle: 'BULLET',
                  textAlign: 'LEFT',
                })
              }
            >
              <ListPlus className='size-4' />
              Add bullet list
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={
                zones.length >= 20 &&
                !zones.some((zone) => zone.style?.isCanvasBackground === true)
              }
              onClick={addCanvasBackground}
            >
              <PaintBucket className='size-4' />
              Canvas background
            </Button>
          </div>
        ) : (
          <p className='rounded-lg border bg-muted/30 px-3 py-2 text-xs text-muted-foreground'>
            Use a Custom canvas to drag, resize, layer, and add zones. Fixed
            templates keep production-safe geometry but still support styling.
          </p>
        )}
        <div
          ref={canvasRef}
          className={`relative mx-auto overflow-hidden rounded-xl border-2 border-zinc-700 bg-zinc-950 shadow-2xl select-none ${
            orientation === 'PORTRAIT'
              ? 'aspect-[9/16] max-h-[68vh]'
              : 'aspect-video w-full'
          }`}
          style={{
            containerType: 'inline-size',
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)',
            backgroundSize: '5% 5%',
          }}
          onPointerMove={continueInteraction}
          onPointerUp={endInteraction}
          onPointerCancel={endInteraction}
          onPointerLeave={(event) => {
            if (event.buttons === 0) endInteraction()
          }}
        >
          {[...zones]
            .sort((a, b) => a.zIndex - b.zIndex)
            .map((zone) => {
              const Icon = zoneIcons[zone.type]
              const isSelected = zone.id === selected?.id
              const text = styleString(zone.style, 'text', zone.name)
              const background = styleString(
                zone.style,
                'background',
                '#18181b'
              )
              const backgroundTransparent =
                zone.style?.backgroundTransparent === true
              const color = styleString(zone.style, 'color', '#ffffff')
              const fontSize = styleNumber(zone.style, 'fontSize', 32)
              const lineHeight = styleNumber(zone.style, 'lineHeight', 1.2)
              const fontFamily = styleString(
                zone.style,
                'fontFamily',
                'Hind Siliguri'
              )
              const textAlign = styleString(zone.style, 'textAlign', 'CENTER')
              const verticalAlign = styleString(
                zone.style,
                'verticalAlign',
                'CENTER'
              )
              const listStyle = styleString(zone.style, 'listStyle', 'NONE')
              const padding = backgroundTransparent
                ? 0
                : styleNumber(zone.style, 'padding', 16)
              const asset = media.find(
                (candidate) =>
                  candidate.id === styleString(zone.style, 'assetId', '')
              )
              return (
                <div
                  key={zone.id}
                  role='button'
                  tabIndex={0}
                  aria-label={`Select ${zone.name}`}
                  className={`absolute overflow-hidden border-2 ${
                    isSelected
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-white/20'
                  } ${animationClass(zone.style)} ${freeform ? 'cursor-move touch-none' : ''}`}
                  style={{
                    left: `${zone.x * 100}%`,
                    top: `${zone.y * 100}%`,
                    width: `${zone.width * 100}%`,
                    height: `${zone.height * 100}%`,
                    zIndex: zone.zIndex,
                    backgroundColor: backgroundTransparent
                      ? 'transparent'
                      : background,
                    color,
                    animationDuration: `${styleNumber(
                      zone.style,
                      'animationDurationMs',
                      650
                    )}ms`,
                    animationDelay: `${styleNumber(
                      zone.style,
                      'entryDelayMs',
                      0
                    )}ms`,
                  }}
                  onClick={() => setSelectedId(zone.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      setSelectedId(zone.id)
                      return
                    }
                    const step = event.shiftKey ? 0.01 : 0.005
                    if (event.key === 'ArrowLeft') nudgeSelected(-step, 0)
                    else if (event.key === 'ArrowRight') nudgeSelected(step, 0)
                    else if (event.key === 'ArrowUp') nudgeSelected(0, -step)
                    else if (event.key === 'ArrowDown') nudgeSelected(0, step)
                    else return
                    event.preventDefault()
                  }}
                  onPointerDown={(event) =>
                    beginInteraction(event, zone, 'move')
                  }
                >
                  {asset?.thumbnailUrl &&
                  (zone.type === 'IMAGE' || zone.type === 'VIDEO') ? (
                    <img
                      src={asset.thumbnailUrl}
                      alt=''
                      className={`size-full ${imageMotionClass(zone.style)}`}
                      style={{
                        objectFit:
                          styleString(zone.style, 'objectFit', 'COVER') ===
                          'CONTAIN'
                            ? 'contain'
                            : styleString(zone.style, 'objectFit', 'COVER') ===
                                'FILL'
                              ? 'fill'
                              : 'cover',
                        ...imageMotionStyle(zone.style),
                      }}
                    />
                  ) : zone.type === 'TEXT' ||
                    zone.type === 'TICKER' ||
                    zone.type === 'DYNAMIC_TIME' ? (
                    <div
                      className={`flex size-full font-semibold ${
                        zone.type === 'TICKER' ? 'whitespace-nowrap' : ''
                      }`}
                      style={{
                        alignItems:
                          verticalAlign === 'TOP'
                            ? 'flex-start'
                            : verticalAlign === 'BOTTOM'
                              ? 'flex-end'
                              : 'center',
                        justifyContent:
                          textAlign === 'LEFT'
                            ? 'flex-start'
                            : textAlign === 'RIGHT'
                              ? 'flex-end'
                              : 'center',
                        textAlign: textAlign.toLowerCase() as
                          'left' | 'center' | 'right',
                        fontSize: canvasFontSize(fontSize),
                        fontWeight: styleNumber(zone.style, 'fontWeight', 700),
                        lineHeight,
                        fontFamily,
                        padding: `${padding}px`,
                        whiteSpace:
                          zone.type === 'TICKER' ? 'nowrap' : 'pre-wrap',
                      }}
                    >
                      <span
                        className={
                          zone.type === 'TICKER' ? 'dotit-ticker-preview' : ''
                        }
                        style={
                          zone.type === 'TICKER'
                            ? {
                                animationDuration: `${clamp(
                                  120 /
                                    styleNumber(zone.style, 'tickerSpeed', 70),
                                  3,
                                  18
                                )}s`,
                              }
                            : undefined
                        }
                      >
                        {zone.type === 'DYNAMIC_TIME' ? (
                          <DynamicTimePreview style={zone.style} />
                        ) : (
                          displayText(text, listStyle)
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className='grid size-full place-items-center bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.12),_transparent_65%)] text-center text-[10px] font-medium tracking-widest text-white/70 uppercase sm:text-xs'>
                      <div>
                        <Icon className='mx-auto mb-1 size-5' />
                        {zone.name}
                      </div>
                    </div>
                  )}
                  {freeform && isSelected ? (
                    <button
                      type='button'
                      aria-label={`Resize ${zone.name}`}
                      className='absolute right-0 bottom-0 size-5 cursor-nwse-resize touch-none border-t border-l border-white/60 bg-primary'
                      onPointerDown={(event) =>
                        beginInteraction(event, zone, 'resize')
                      }
                    />
                  ) : null}
                </div>
              )
            })}
        </div>
        <p className='text-center text-xs text-muted-foreground'>
          {freeform
            ? 'Drag to move, use the red corner to resize, or use arrow keys for 0.5% nudges (Shift = 1%).'
            : 'Preview uses the same normalized coordinates as the Android player.'}
        </p>
      </div>

      <aside className='grid gap-4 rounded-xl border bg-card p-4 md:grid-cols-2 xl:grid-cols-4'>
        {selected ? (
          <>
            <section className='space-y-3 rounded-lg border bg-muted/20 p-3 md:col-span-2 xl:col-span-4'>
              <div className='flex items-center justify-between gap-3'>
                <div>
                  <p className='text-sm font-semibold'>Layer stack</p>
                  <p className='text-xs text-muted-foreground'>
                    Front layers are listed first. Select any layer to edit it.
                  </p>
                </div>
                <span className='rounded-full bg-background px-2 py-1 text-xs font-medium'>
                  {zones.length} layers
                </span>
              </div>
              <div className='grid gap-2 sm:grid-cols-2 xl:grid-cols-4'>
                {[...zones]
                  .sort((a, b) => b.zIndex - a.zIndex)
                  .map((zone, index) => {
                    const Icon = zoneIcons[zone.type]
                    const isActive = zone.id === selected.id
                    return (
                      <button
                        key={zone.id}
                        type='button'
                        aria-label={`Edit layer ${zone.name}`}
                        className={`flex min-w-0 items-center gap-2 rounded-lg border p-2 text-left transition-colors ${
                          isActive
                            ? 'border-primary bg-primary/10'
                            : 'bg-background hover:bg-muted'
                        }`}
                        onClick={() => setSelectedId(zone.id)}
                      >
                        <Icon className='size-4 shrink-0' />
                        <span className='min-w-0 flex-1'>
                          <span className='block truncate text-sm font-medium'>
                            {zone.name}
                          </span>
                          <span className='block text-xs text-muted-foreground'>
                            {zone.type.toLowerCase()} · level {zone.zIndex + 1}
                          </span>
                        </span>
                        {index === 0 ? (
                          <span className='text-[10px] font-semibold text-red-600 uppercase'>
                            Front
                          </span>
                        ) : null}
                      </button>
                    )
                  })}
              </div>
            </section>
            <div className='flex items-center justify-between gap-2 md:col-span-2 xl:col-span-4'>
              <div>
                <p className='text-xs font-medium tracking-wide text-muted-foreground uppercase'>
                  Selected layer
                </p>
                <p className='font-semibold'>{selected.name}</p>
              </div>
              <div className='flex flex-wrap justify-end gap-1'>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  disabled={!freeform || zones.length >= 20}
                  onClick={duplicateSelected}
                  aria-label='Duplicate selected layer'
                  title={
                    freeform
                      ? 'Duplicate selected layer'
                      : 'Fixed templates have a locked layer count'
                  }
                >
                  <Copy className='size-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  disabled={
                    !freeform ||
                    selected.zIndex >=
                      Math.max(...zones.map((zone) => zone.zIndex))
                  }
                  onClick={() => moveLayer('front')}
                  aria-label='Bring layer to front'
                  title='Bring to front'
                >
                  <BringToFront className='size-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  disabled={
                    !freeform ||
                    selected.zIndex >=
                      Math.max(...zones.map((zone) => zone.zIndex))
                  }
                  onClick={() => moveLayer('forward')}
                  aria-label='Move layer up one level'
                  title='Move up one level'
                >
                  <ArrowUp className='size-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  disabled={
                    !freeform ||
                    selected.zIndex <=
                      Math.min(...zones.map((zone) => zone.zIndex))
                  }
                  onClick={() => moveLayer('backward')}
                  aria-label='Move layer down one level'
                  title='Move down one level'
                >
                  <ArrowDown className='size-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  disabled={
                    !freeform ||
                    selected.zIndex <=
                      Math.min(...zones.map((zone) => zone.zIndex))
                  }
                  onClick={() => moveLayer('back')}
                  aria-label='Send layer to back'
                  title='Send to back'
                >
                  <SendToBack className='size-4' />
                </Button>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  disabled={!freeform || zones.length === 1}
                  onClick={removeSelected}
                  aria-label='Delete selected layer'
                  title={
                    freeform
                      ? 'Delete selected layer'
                      : 'Fixed templates have a locked layer count'
                  }
                >
                  <Trash2 className='size-4' />
                </Button>
              </div>
            </div>
            <p className='rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground md:col-span-2 xl:col-span-4'>
              Layer {selected.zIndex + 1} of {zones.length}
              {!freeform ? ' · fixed template layer count is locked' : ''}
            </p>

            <div className='space-y-2'>
              <Label htmlFor='layer-name'>Layer name</Label>
              <Input
                id='layer-name'
                value={selected.name}
                onChange={(event) =>
                  replaceZone(selected.id, { name: event.target.value })
                }
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='layer-type'>Content type</Label>
              <select
                id='layer-type'
                className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                value={selected.type}
                onChange={(event) =>
                  replaceZone(selected.id, {
                    type: event.target.value as BuilderZoneType,
                  })
                }
              >
                {(
                  [
                    'IMAGE',
                    'VIDEO',
                    'TEXT',
                    'TICKER',
                    'DYNAMIC_TIME',
                  ] as BuilderZoneType[]
                ).map((type) => (
                  <option
                    key={type}
                    value={type}
                    disabled={
                      type === 'VIDEO' &&
                      zones.some(
                        (zone) =>
                          zone.type === 'VIDEO' && zone.id !== selected.id
                      )
                    }
                  >
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {freeform ? (
              <div className='space-y-3 md:col-span-2'>
                <div className='space-y-2'>
                  <Label>Position on canvas</Label>
                  <div className='grid grid-cols-3 gap-1'>
                    {(
                      [
                        ['LEFT', 'Left'],
                        ['HCENTER', 'H center'],
                        ['RIGHT', 'Right'],
                        ['TOP', 'Top'],
                        ['VCENTER', 'V center'],
                        ['BOTTOM', 'Bottom'],
                      ] as const
                    ).map(([value, label]) => (
                      <Button
                        key={value}
                        type='button'
                        variant='outline'
                        size='sm'
                        className='h-8 px-2 text-xs'
                        onClick={() => alignSelected(value)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    className='w-full'
                    onClick={fillCanvas}
                  >
                    <Maximize2 className='size-4' />
                    Fill entire canvas
                  </Button>
                </div>
                <div className='grid grid-cols-4 gap-2'>
                  {(['x', 'y', 'width', 'height'] as const).map((property) => (
                    <div className='space-y-1' key={property}>
                      <Label
                        htmlFor={`geometry-${property}`}
                        className='text-xs uppercase'
                      >
                        {property === 'width'
                          ? 'W'
                          : property === 'height'
                            ? 'H'
                            : property}
                      </Label>
                      <Input
                        id={`geometry-${property}`}
                        type='number'
                        min={
                          property === 'width' || property === 'height' ? 5 : 0
                        }
                        max={100}
                        step={0.5}
                        value={Math.round(selected[property] * 1000) / 10}
                        onChange={(event) =>
                          numericGeometry(property, Number(event.target.value))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {selected.type === 'TEXT' || selected.type === 'TICKER' ? (
              <div className='space-y-3 md:col-span-2'>
                <div className='space-y-2'>
                  <Label htmlFor='layer-copy'>
                    Text (one list item per line)
                  </Label>
                  <textarea
                    id='layer-copy'
                    className='min-h-20 w-full rounded-md border bg-background px-3 py-2 text-sm'
                    value={styleString(selected.style, 'text', '')}
                    maxLength={4000}
                    onChange={(event) =>
                      replaceStyle(selected.id, { text: event.target.value })
                    }
                  />
                </div>
                {selected.type === 'TEXT' ? (
                  <div className='space-y-2'>
                    <Label htmlFor='list-style'>Bullet / icon style</Label>
                    <select
                      id='list-style'
                      className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                      value={styleString(selected.style, 'listStyle', 'NONE')}
                      onChange={(event) =>
                        replaceStyle(selected.id, {
                          listStyle: event.target.value,
                        })
                      }
                    >
                      <option value='NONE'>No bullets</option>
                      <option value='BULLET'>Round bullet •</option>
                      <option value='CHECK'>Check mark ✓</option>
                      <option value='ARROW'>Arrow →</option>
                      <option value='STAR'>Star ★</option>
                    </select>
                  </div>
                ) : null}
              </div>
            ) : null}

            {selected.type === 'IMAGE' || selected.type === 'VIDEO' ? (
              <div className='space-y-2'>
                <Label htmlFor='layer-media'>Media</Label>
                <select
                  id='layer-media'
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={styleString(selected.style, 'assetId', '')}
                  required
                  onChange={(event) =>
                    replaceStyle(selected.id, { assetId: event.target.value })
                  }
                >
                  <option value=''>Select {selected.type.toLowerCase()}</option>
                  {media
                    .filter((asset) => asset.kind === selected.type)
                    .map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.name}
                      </option>
                    ))}
                </select>
                {!media.some((asset) => asset.kind === selected.type) ? (
                  <p className='text-xs text-amber-600'>
                    Upload a READY {selected.type.toLowerCase()} in Media first.
                  </p>
                ) : null}
              </div>
            ) : null}

            {selected.type === 'IMAGE' ? (
              <div className='grid gap-3 md:col-span-2 xl:col-span-4 xl:grid-cols-4'>
                <div className='space-y-2'>
                  <Label htmlFor='image-motion'>Still-image motion</Label>
                  <select
                    id='image-motion'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(selected.style, 'imageMotion', 'NONE')}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        imageMotion: event.target.value,
                      })
                    }
                  >
                    <option value='NONE'>None</option>
                    <option value='KEN_BURNS'>Ken Burns · pan & zoom</option>
                    <option value='PARALLAX'>3D parallax</option>
                    <option value='SLOW_DRIFT'>Slow drift</option>
                    <option value='PERSPECTIVE_TILT'>Perspective tilt</option>
                  </select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='motion-duration'>Duration (seconds)</Label>
                  <Input
                    id='motion-duration'
                    type='number'
                    min={2}
                    max={60}
                    step={1}
                    value={Math.round(
                      styleNumber(selected.style, 'motionDurationMs', 12_000) /
                        1_000
                    )}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        motionDurationMs: Number(event.target.value) * 1_000,
                      })
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='motion-intensity'>Intensity (1–10)</Label>
                  <Input
                    id='motion-intensity'
                    type='number'
                    min={1}
                    max={10}
                    value={styleNumber(selected.style, 'motionIntensity', 5)}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        motionIntensity: Number(event.target.value),
                      })
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='motion-direction'>Motion direction</Label>
                  <select
                    id='motion-direction'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(
                      selected.style,
                      'motionDirection',
                      'LEFT_RIGHT'
                    )}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        motionDirection: event.target.value,
                      })
                    }
                  >
                    <option value='LEFT_RIGHT'>Left → right</option>
                    <option value='RIGHT_LEFT'>Right → left</option>
                    <option value='UP_DOWN'>Top → bottom</option>
                    <option value='DOWN_UP'>Bottom → top</option>
                  </select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='motion-focus'>Focal point</Label>
                  <select
                    id='motion-focus'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(
                      selected.style,
                      'motionFocalPoint',
                      'CENTER'
                    )}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        motionFocalPoint: event.target.value,
                      })
                    }
                  >
                    <option value='CENTER'>Center</option>
                    <option value='LEFT'>Left</option>
                    <option value='RIGHT'>Right</option>
                    <option value='TOP'>Top</option>
                    <option value='BOTTOM'>Bottom</option>
                  </select>
                </div>
                <label className='flex items-center gap-2 self-end rounded-md border px-3 py-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={selected.style?.motionLoop !== false}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        motionLoop: event.target.checked,
                      })
                    }
                  />
                  Loop continuously
                </label>
              </div>
            ) : null}

            {selected.type === 'DYNAMIC_TIME' ? (
              <div className='grid gap-3 md:col-span-2 xl:col-span-4 xl:grid-cols-4'>
                <div className='space-y-2'>
                  <Label htmlFor='timer-mode'>Timer mode</Label>
                  <select
                    id='timer-mode'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(
                      selected.style,
                      'timerMode',
                      'COUNTDOWN'
                    )}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        timerMode: event.target.value,
                      })
                    }
                  >
                    <option value='COUNTDOWN'>Daily countdown</option>
                    <option value='FIXED'>Fixed time</option>
                  </select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='target-time'>Target / display time</Label>
                  <Input
                    id='target-time'
                    type='time'
                    value={styleString(selected.style, 'targetTime', '13:15')}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        targetTime: event.target.value,
                      })
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='timer-timezone'>Timezone</Label>
                  <Input
                    id='timer-timezone'
                    value={styleString(
                      selected.style,
                      'timezone',
                      'Asia/Dhaka'
                    )}
                    placeholder='Asia/Dhaka'
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        timezone: event.target.value,
                      })
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='timer-locale'>Digits</Label>
                  <select
                    id='timer-locale'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(selected.style, 'locale', 'bn-BD')}
                    onChange={(event) =>
                      replaceStyle(selected.id, { locale: event.target.value })
                    }
                  >
                    <option value='bn-BD'>বাংলা digits</option>
                    <option value='en-GB'>English digits</option>
                  </select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='timer-hour-cycle'>Fixed time format</Label>
                  <select
                    id='timer-hour-cycle'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(selected.style, 'hourCycle', 'H12')}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        hourCycle: event.target.value,
                      })
                    }
                  >
                    <option value='H12'>12 hour</option>
                    <option value='H24'>24 hour</option>
                  </select>
                </div>
                <label className='flex items-center gap-2 self-end rounded-md border px-3 py-2 text-sm'>
                  <input
                    type='checkbox'
                    checked={selected.style?.showSeconds !== false}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        showSeconds: event.target.checked,
                      })
                    }
                  />
                  Show seconds
                </label>
              </div>
            ) : null}

            <div className='grid grid-cols-2 gap-3'>
              <div className='space-y-2'>
                <Label htmlFor='layer-background'>Background</Label>
                <Input
                  id='layer-background'
                  type='color'
                  className='p-1'
                  value={styleString(selected.style, 'background', '#18181b')}
                  onChange={(event) =>
                    replaceStyle(selected.id, {
                      background: event.target.value,
                    })
                  }
                  disabled={selected.style?.backgroundTransparent === true}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='layer-color'>Font color</Label>
                <Input
                  id='layer-color'
                  type='color'
                  className='p-1'
                  disabled={
                    selected.type === 'IMAGE' || selected.type === 'VIDEO'
                  }
                  value={styleString(selected.style, 'color', '#ffffff')}
                  onChange={(event) =>
                    replaceStyle(selected.id, { color: event.target.value })
                  }
                />
              </div>
            </div>

            {selected.type === 'TEXT' ||
            selected.type === 'TICKER' ||
            selected.type === 'DYNAMIC_TIME' ? (
              <label className='flex items-center gap-2 rounded-md border px-3 py-2 text-sm md:col-span-2'>
                <input
                  type='checkbox'
                  checked={selected.style?.backgroundTransparent === true}
                  onChange={(event) =>
                    replaceStyle(selected.id, {
                      backgroundTransparent: event.target.checked,
                    })
                  }
                />
                No background / transparent
              </label>
            ) : null}

            {selected.type === 'TEXT' ||
            selected.type === 'TICKER' ||
            selected.type === 'DYNAMIC_TIME' ? (
              <div className='grid grid-cols-2 gap-3 md:col-span-2 xl:col-span-4 xl:grid-cols-4'>
                <div className='space-y-2'>
                  <Label htmlFor='font-size'>Font size</Label>
                  <Input
                    id='font-size'
                    type='number'
                    min={8}
                    max={240}
                    value={styleNumber(selected.style, 'fontSize', 32)}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        fontSize: Number(event.target.value),
                      })
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='font-weight'>Font weight</Label>
                  <select
                    id='font-weight'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleNumber(selected.style, 'fontWeight', 700)}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        fontWeight: Number(event.target.value),
                      })
                    }
                  >
                    <option value={400}>Regular</option>
                    <option value={600}>Semibold</option>
                    <option value={700}>Bold</option>
                    <option value={900}>Black</option>
                  </select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='line-height'>Line height</Label>
                  <Input
                    id='line-height'
                    type='number'
                    min={0.8}
                    max={3}
                    step={0.1}
                    value={styleNumber(selected.style, 'lineHeight', 1.2)}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        lineHeight: Number(event.target.value),
                      })
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='font-family'>Font family</Label>
                  <SignageFontSelect
                    id='font-family'
                    value={styleString(
                      selected.style,
                      'fontFamily',
                      'Hind Siliguri'
                    )}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        fontFamily: event.target.value,
                      })
                    }
                  />
                </div>{' '}
                <div className='space-y-2'>
                  <Label htmlFor='text-align'>Alignment</Label>
                  <select
                    id='text-align'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(selected.style, 'textAlign', 'CENTER')}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        textAlign: event.target.value,
                      })
                    }
                  >
                    <option value='LEFT'>Left</option>
                    <option value='CENTER'>Center</option>
                    <option value='RIGHT'>Right</option>
                  </select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='vertical-align'>Vertical alignment</Label>
                  <select
                    id='vertical-align'
                    className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                    value={styleString(
                      selected.style,
                      'verticalAlign',
                      'CENTER'
                    )}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        verticalAlign: event.target.value,
                      })
                    }
                  >
                    <option value='TOP'>Top</option>
                    <option value='CENTER'>Center</option>
                    <option value='BOTTOM'>Bottom</option>
                  </select>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='text-padding'>Inner padding</Label>
                  <Input
                    id='text-padding'
                    type='number'
                    min={0}
                    max={64}
                    value={styleNumber(selected.style, 'padding', 16)}
                    onChange={(event) =>
                      replaceStyle(selected.id, {
                        padding: Number(event.target.value),
                      })
                    }
                  />
                </div>
                {selected.type === 'TICKER' ? (
                  <div className='space-y-2'>
                    <Label htmlFor='ticker-speed'>Ticker speed</Label>
                    <Input
                      id='ticker-speed'
                      type='number'
                      min={10}
                      max={300}
                      value={styleNumber(selected.style, 'tickerSpeed', 70)}
                      onChange={(event) =>
                        replaceStyle(selected.id, {
                          tickerSpeed: Number(event.target.value),
                        })
                      }
                    />
                  </div>
                ) : null}
              </div>
            ) : (
              <div className='space-y-2'>
                <Label htmlFor='object-fit'>Image/video fit</Label>
                <select
                  id='object-fit'
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={styleString(selected.style, 'objectFit', 'COVER')}
                  onChange={(event) =>
                    replaceStyle(selected.id, { objectFit: event.target.value })
                  }
                >
                  <option value='COVER'>Cover</option>
                  <option value='CONTAIN'>Contain</option>
                  <option value='FILL'>Stretch</option>
                </select>
              </div>
            )}

            <div className='grid grid-cols-2 gap-3 md:col-span-2 xl:col-span-4 xl:grid-cols-4'>
              <div className='space-y-2'>
                <Label htmlFor='entry-animation'>Layer entry animation</Label>
                <select
                  id='entry-animation'
                  className='h-9 w-full rounded-md border bg-background px-3 text-sm'
                  value={styleString(selected.style, 'entryAnimation', 'NONE')}
                  onChange={(event) =>
                    replaceStyle(selected.id, {
                      entryAnimation: event.target.value,
                    })
                  }
                >
                  <option value='NONE'>None</option>
                  <option value='FADE_IN'>Fade in</option>
                  <option value='SLIDE_LEFT'>Slide from left</option>
                  <option value='SLIDE_RIGHT'>Slide from right</option>
                  <option value='SLIDE_TOP'>Slide from top</option>
                  <option value='SLIDE_BOTTOM'>Slide from bottom</option>
                  <option value='ZOOM_IN'>Zoom in</option>
                </select>
              </div>
              <div className='space-y-2'>
                <Label htmlFor='animation-duration'>Duration (ms)</Label>
                <Input
                  id='animation-duration'
                  type='number'
                  min={200}
                  max={3000}
                  step={50}
                  value={styleNumber(
                    selected.style,
                    'animationDurationMs',
                    650
                  )}
                  onChange={(event) =>
                    replaceStyle(selected.id, {
                      animationDurationMs: Number(event.target.value),
                    })
                  }
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='entry-delay'>Start delay (ms)</Label>
                <Input
                  id='entry-delay'
                  type='number'
                  min={0}
                  max={10000}
                  step={50}
                  value={styleNumber(selected.style, 'entryDelayMs', 0)}
                  onChange={(event) =>
                    replaceStyle(selected.id, {
                      entryDelayMs: Number(event.target.value),
                    })
                  }
                />
              </div>
            </div>
          </>
        ) : (
          <div className='grid min-h-48 place-items-center text-center text-sm text-muted-foreground'>
            <div>
              <Plus className='mx-auto mb-2 size-6' />
              Add a layer to start designing.
            </div>
          </div>
        )}
      </aside>
    </div>
  )
}
